import type { IsoDateTimeString } from './types';

const ISO_RE = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d{1,9})?(?:Z|[+-]\d{2}:\d{2})$/;

export function isIsoDateTime(value: unknown): value is IsoDateTimeString {
  return typeof value === 'string' && ISO_RE.test(value);
}

export function toDate(value: IsoDateTimeString | string): Date {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    throw new TypeError(`Invalid ISO date-time: ${value}`);
  }
  return date;
}

export function nowIso(): IsoDateTimeString {
  return new Date().toISOString() as IsoDateTimeString;
}

/** Parts of a date in a given IANA timezone, without any library. */
export function zonedParts(date: Date, timeZone: string) {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(date);
  const pick = (type: Intl.DateTimeFormatPartTypes) =>
    parts.find((p) => p.type === type)?.value ?? '00';
  return {
    year: Number(pick('year')),
    month: Number(pick('month')),
    day: Number(pick('day')),
    hour: Number(pick('hour')),
    minute: Number(pick('minute')),
    second: Number(pick('second')),
  };
}

/** "YYYY-MM-DD" for a date as seen in a timezone (for date pickers and report ranges). */
export function toZonedDateKey(date: Date, timeZone: string): string {
  const { year, month, day } = zonedParts(date, timeZone);
  return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

const LOCAL_INPUT = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/;
const pad2 = (n: number) => String(n).padStart(2, '0');

/** An instant as a `datetime-local` value ("YYYY-MM-DDTHH:mm") on a timezone's wall clock. */
export function toZonedInput(iso: string | null, timeZone: string): string {
  if (!iso) return '';
  const p = zonedParts(new Date(iso), timeZone);
  return `${p.year}-${pad2(p.month)}-${pad2(p.day)}T${pad2(p.hour)}:${pad2(p.minute)}`;
}

function zoneOffsetMs(at: number, timeZone: string): number {
  const p = zonedParts(new Date(at), timeZone);
  return Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute, p.second) - at;
}

/**
 * A `datetime-local` value read on a timezone's wall clock, as an ISO instant; null when the
 * value is not "YYYY-MM-DDTHH:mm". The offset is looked up twice so a time next to a DST switch
 * lands on the right side of it; a wall time the switch skips resolves to just after the gap.
 */
export function fromZonedInput(value: string, timeZone: string): string | null {
  const match = LOCAL_INPUT.exec(value);
  if (!match) return null;
  const [, y, mo, d, h, mi] = match.map(Number) as [number, number, number, number, number, number];
  const wall = Date.UTC(y, mo - 1, d, h, mi);
  const first = wall - zoneOffsetMs(wall, timeZone);
  const second = wall - zoneOffsetMs(first, timeZone);
  for (const candidate of [second, first]) {
    const iso = new Date(candidate).toISOString();
    if (toZonedInput(iso, timeZone) === value) return iso;
  }
  // Neither guess reads back only for a wall time the switch skips; the later one is past the gap.
  return new Date(Math.max(first, second)).toISOString();
}
